import { CreateQuoteRequest } from 'api/accessexpressv3/schemas/QuotesSchemas';
import { useAuthUser } from 'hooks/useAuthUser';
import { CartSiteAddress, CartStore } from '../CartProvider';
import { ConfigRequirements } from '../BaseComponents/Cart/locationFlow/access/ConfigRequirementsModal/schema';
import { DraftOrderAPIResponse } from 'api/accessexpressv3/schemas/OrdersSchemas';
import { postQuote, updateOrder, postOrder } from 'api/accessexpressv3/api';
import { buildItemPayload, downloadOptions } from '../utils';
import { ProductCategory } from '../accessExpress.types';

type Product = {
  product_type: ProductCategory;
} & Partial<ConfigRequirements>;

function getSortedDownloadOptions() {
  return [...downloadOptions].sort((a, b) => Number(a.value) - Number(b.value));
}

function getDIADownloadRange(selectedValue: string): [string, string] {
  const sortedDownloadOptions = getSortedDownloadOptions();

  const index = sortedDownloadOptions.findIndex(
    (opt) => opt.value === selectedValue,
  );
  if (index === -1) return [selectedValue, selectedValue];

  const prev = sortedDownloadOptions[index - 1]?.value;
  const next = sortedDownloadOptions[index + 1]?.value;

  return [prev ?? selectedValue, next ?? selectedValue];
}
export const postQuoteToRequest = (
  userInfo: ReturnType<typeof useAuthUser>,
  locations: Array<CartSiteAddress>,
  products: Product[],
): CreateQuoteRequest => {
  return {
    customer_name: userInfo.companyName,
    requester: `${userInfo.first_name} ${userInfo.last_name}`,
    business_unit: 'standard',
    email: userInfo.email ?? '',
    locations: locations.map((location) => {
      return {
        macnum: location.macnum ?? '',
        ...(location.parent_macnum && {
          parent_macnum: location.parent_macnum,
        }),
        address1: location.address_line_1 ?? '',
        city: location.city ?? '',
        state: location.state ?? '',
        zip_code: location.zip ?? '',
        external_id: location.external_id ?? '',
        products: products.map((product) => {
          if (product.product_type === 'Starlink') {
            return {
              product_type: product.product_type,
            };
          }

          const [min, max] =
            product.product_type === 'DIA'
              ? getDIADownloadRange(product?.download?.value as string)
              : [
                  product?.download?.value ?? '',
                  product?.download?.value ?? '',
                ];
          return {
            ...product,
            product_type: product.product_type,
            term: product?.term?.value,
            exclude_carriers: product?.exclude_carriers?.map(
              (item) => item.value,
            ),
            preferred_carriers: product?.preferred_carriers?.map(
              (item) => item.value,
            ),
            include_carriers: product?.preferred_carriers?.map(
              (item) => item.value,
            ),
            ip_type: product?.ip_type?.value,
            ip_block: product?.ip_block?.value,
            min_download: min,
            max_download: max,
          };
        }),
      };
    }),
  };
};

export const upsertDraft = async (
  userInfo: ReturnType<typeof useAuthUser>,
  cart: CartStore,
): Promise<DraftOrderAPIResponse | null> => {
  const payload = {
    additional_details: cart.additionalDetails ?? {},
    locations: cart.locations.map((location) => ({
      address: { ...location, id: location.id as string },
      order_items: cart
        .itemsByLocation(location.id)
        .filter((item) => item.category === 'Access')
        .map(buildItemPayload),
    })),
  };

  return cart.draftId
    ? await updateOrder(payload, cart.draftId)
    : await postOrder({
        ...payload,
        customer_name: userInfo.companyName,
        requester: `${userInfo.first_name} ${userInfo.last_name}`,
        status: 'draft',
      });
};

export const createQuote = async ({
  userInfo,
  cart,
}: {
  userInfo: ReturnType<typeof useAuthUser>;
  cart: CartStore;
}): Promise<string> => {
  const quoteResponse = await postQuote(
    postQuoteToRequest(userInfo, cart.locations, [
      {
        product_type: cart.selectedAccessProductCategory as 'Broadband' | 'DIA',
        ...cart.additionalDetails?.configs,
      },
    ]),
  );

  return quoteResponse.id;
};

export const updateDraftWithQuoteId = async ({
  cart,
  quoteId,
  draftId,
}: {
  cart: CartStore;
  quoteId: string;
  draftId: string;
}) => {
  const additionalDetails = {
    configs: cart.additionalDetails?.configs,
    ...(cart.selectedAccessProductCategory === 'DIA'
      ? { dia_quote_request: quoteId }
      : { bb_quote_request: quoteId }),
  };

  return await updateOrder({ additional_details: additionalDetails }, draftId);
};
